package dev.youndie.site.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobwebx.markdown.markdown
import dev.youndie.site.components.widgets.Chip
import dev.youndie.site.components.widgets.Prompt
import dev.youndie.site.model.LIBRARIES
import dev.youndie.site.model.SITE_NAME
import dev.youndie.site.model.SITE_URL
import dev.youndie.site.model.softwareSourceCodeJsonLd
import org.jetbrains.compose.web.dom.Article
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * One page per library, written as Markdown at the root of the markdown folder so the
 * route is `/tracy` rather than something nested.
 *
 * The tagline and the tags come from the model rather than being repeated in front
 * matter. Two copies of the same sentence is two chances to describe the library
 * differently, and the card and the page are exactly where that would show.
 */
@InitRoute
fun initLibraryLayout(ctx: InitRouteContext) {
    val frontMatter = ctx.markdown?.frontMatter
    val name = frontMatter?.get("library")?.singleOrNull()
    val library = LIBRARIES.firstOrNull { it.name == name }
    val title = frontMatter?.get("title")?.singleOrNull() ?: name ?: SITE_NAME
    val searchTitle = frontMatter?.get("searchTitle")?.singleOrNull() ?: title
    val description = frontMatter?.get("description")?.singleOrNull() ?: library?.tagline
    val route = ctx.route.path.trimEnd('/')

    ctx.data.add(
        PageLayoutData(
            hasMarkdownCopy = true,
            title = "$searchTitle — $SITE_NAME",
            socialTitle = title,
            description = description,
            jsonLd = library?.let {
                softwareSourceCodeJsonLd(
                    name = it.name,
                    description = description ?: it.tagline,
                    url = SITE_URL + route,
                    repository = it.repo,
                    languages = listOf("Kotlin"),
                )
            },
        )
    )
}

@Layout(".components.layouts.PageLayout")
@Composable
fun LibraryLayout(ctx: PageContext, content: @Composable () -> Unit) {
    val frontMatter = ctx.markdown?.frontMatter
    val name = frontMatter?.get("library")?.singleOrNull()
    val library = LIBRARIES.firstOrNull { it.name == name }
    val post = frontMatter?.get("post")?.singleOrNull()
    val docs = frontMatter?.get("docs")?.singleOrNull()

    Header(attrs = { classes("hero") }) {
        Prompt("cat ${name ?: "readme"}/README.md", path = "~/src")
        H1 { Text(name ?: "library") }

        if (library != null) {
            Div(attrs = { classes("role") }) { Text(library.tags.joinToString(" · ")) }
            Div(attrs = { classes("lib-lede") }) { Text(library.tagline) }
        }

        Div(attrs = { classes("hero-links") }) {
            if (library != null) Chip(library.repo, "github/${library.name}")
            if (post != null) Chip(post, "read the write-up")
            if (docs != null) Chip(docs, "api docs")
            Chip("/", "all libraries")
        }
    }

    Article(attrs = { classes("prose") }) {
        content()
    }

    // Every library page ends pointing back into the site rather than only outward,
    // which is the difference between twelve pages and twelve dead ends.
    Div(attrs = { classes("lib-more") }) {
        Span(attrs = { classes("lib-more-label") }) { Text("more") }
        Div(attrs = { classes("hero-links") }) {
            LIBRARIES.filter { it.name != name }.take(6).forEach { other ->
                Chip(other.page, other.name)
            }
        }
    }
}
